import * as React from 'react';
import {TransitionGroup} from 'react-transition-group';

// @ts-expect-error - TS7031 - Binding element 'children' implicitly has an 'any' type.
const OnlyChild = ({children}) => React.Children.toArray(children)[0] || null;

/**
 * NOTE (): the child(ren) needs to have CSS property transition with height in order for this to work
 */
export const Expander = ({
  duration,
  children,
}: {
  duration?: number;
  children?: React.ReactElement<any>;
}) => {
  const child = React.Children.toArray(children)[0] || null;
  return (
    // @ts-expect-error - TS2769 - No overload matches this call.
    <TransitionGroup component={OnlyChild}>
      {/* @ts-expect-error - TS2322 - Type 'string | number | bigint | ReactElement<unknown, string | JSXElementConstructor<any>> | Iterable<ReactNode> | Promise<AwaitedReactNode>' is not assignable to type 'ReactElement<unknown, string | JSXElementConstructor<any>> | ((arg1: { style: { [key: string]: string | number; } | null | undefined; ref: Ref<HTMLElement>; }, arg2: ExpanderStatus) => ReactNode)'. */}
      {child && <Expandee duration={duration}>{child}</Expandee>}
    </TransitionGroup>
  );
};
export default Expander;

export const Expanders = ({
  duration,
  children,
  ...props
}: {
  duration?: number;
  children?: React.ReactElement<any>;
}) => (
  <TransitionGroup {...props}>
    {React.Children.map(
      children,
      (child) => child && <Expandee duration={duration}>{child}</Expandee>,
    )}
  </TransitionGroup>
);

type ExpanderStatus =
  | 'unmounted'
  | 'prerendering'
  | 'preentering'
  | 'entering'
  | 'entered'
  | 'exiting'
  | 'exited';

/**
 * this component functions like a Transition. see the
 * react-transition-group docs for more info.
 */
function Expandee({
  in: inProp,
  duration = 200,
  children,
}: {
  in?: boolean;
  duration?: number;
  children:
    | ((
        arg1: {
          style:
            | {
                [key: string]: string | number;
              }
            | null
            | undefined;
          ref: React.Ref<HTMLElement>;
        },
        arg2: ExpanderStatus,
      ) => React.ReactNode)
    | React.ReactElement;
}) {
  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const childRef = React.useRef<HTMLElement | null | undefined>();
  const [state, setState] = React.useState<{
    status: ExpanderStatus;
    height: number;
  }>({
    status: 'unmounted',
    height: 0,
  });
  const {status, height} = state;

  React.useEffect(() => {
    if (inProp) {
      if (status === 'unmounted') {
        setState({...state, status: 'prerendering'});
      } else if (status === 'prerendering') {
        const height = childRef.current?.offsetHeight;
        setState({
          ...state,
          status: 'preentering',
          // @ts-expect-error - TS2322 - Type 'number | undefined' is not assignable to type 'number'.
          height,
        });
      } else if (status === 'preentering') {
        // reflow
        childRef.current?.offsetHeight;
        setState({...state, status: 'entering'});
      } else if (status === 'entering') {
        setTimeout(() => {
          setState({
            ...state,
            status: 'entered',
          });
        }, duration);
      }
    } else {
      if (status === 'entered') {
        const height = childRef.current?.offsetHeight;
        setState({
          ...state,
          status: 'exiting',
          // @ts-expect-error - TS2322 - Type 'number | undefined' is not assignable to type 'number'.
          height,
        });
      } else if (status === 'exiting') {
        // reflow
        childRef.current?.offsetHeight;
        setState({...state, status: 'exited', height: 0});
      } else if (status === 'exited') {
        setTimeout(() => {
          setState({...state, status: 'unmounted'});
        }, duration);
      }
    }
  }, [inProp, state]);

  if (status === 'unmounted') {
    return null;
  }

  let style;

  if (status === 'prerendering') {
    style = {
      position: 'absolute',
      height: 'auto',
      visibility: 'hidden',
    };
  } else if (status === 'preentering') {
    style = {
      height: 0,
      overflow: 'hidden',
    };
  } else if (['entering', 'exiting', 'exited'].includes(status)) {
    style = {
      height,
      overflow: 'hidden',
    };
  }

  if (typeof children === 'function') {
    // @ts-expect-error - TS2322 - Type 'RefObject<HTMLElement | null | undefined>' is not assignable to type 'Ref<HTMLElement>'.
    return children({style, ref: childRef}, status);
  } else {
    const child = React.Children.only(children);
    // @ts-expect-error - TS2769 - No overload matches this call.
    return React.cloneElement(child, {style, ref: childRef});
  }
}
